<script lang="ts">
  import { store } from '../lib/store.svelte.ts';
  import { router } from '../lib/router.svelte.ts';
  import { qrSvg } from '../lib/qr.ts';
  import { daysOnSite } from '../lib/format.ts';
  import CorrectionSheet from '../lib/CorrectionSheet.svelte';
  import type { Cart } from '@chariot/contract';

  let { code }: { code: string } = $props();

  let svg = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);
  let renaming = $state(false);
  let newName = $state('');
  let finding = $state(false);

  const cartState = $derived(store.states.get(code) ?? null);
  const cart = $derived(store.cartByCode.get(code) ?? null);
  const history = $derived(
    store.events
      .filter((e) => e.type === 'cart_scanned' && e.cart_code === code)
      .sort((a, b) => b.at.localeCompare(a.at))
      .slice(0, 50),
  );

  $effect(() => {
    void qrSvg(code).then((s) => (svg = s));
  });

  async function setStatus(status: Cart['status']) {
    busy = true;
    error = null;
    try {
      await store.setCartStatus(code, status);
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }

  async function rename() {
    busy = true;
    error = null;
    try {
      await store.renameCart(code, newName.trim());
      renaming = false;
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }
</script>

<button class="ghost slim no-print" style="margin-left:-.7rem" onclick={() => router.go('carts')}>← Chariots</button>

{#if !cart || !cartState}
  <p class="banner warn">Chariot {code} inconnu sur cet appareil. Synchronisez pour le récupérer.</p>
{:else}
  <h1 style="margin-top:.4rem;margin-bottom:.1rem">{cart.name}</h1>
  <p class="mono small subtle" style="margin-bottom:1rem">{cart.code}</p>

  <div class="card">
    <div class="detail-grid">
      <div class="qr">{@html svg}</div>
      <div class="grow">
        {#if cartState.current_site}
          <div class="desc">
            <div>
              <div class="dt">Site actuel</div>
              <div class="dd">
                <button class="link" onclick={() => router.go(`sites/${cartState.current_site}`)}>
                  {store.siteName(cartState.current_site)}
                </button>
                <span class="mono tiny subtle">{cartState.current_site}</span>
              </div>
            </div>
            <div>
              <div class="dt">Sur place</div>
              <div class="dd">
                {daysOnSite(cartState.days_present)}
                <span class="tiny subtle">({new Date(cartState.arrived_at!).toLocaleDateString('fr-FR')})</span>
              </div>
            </div>
            <div>
              <div class="dt">Dernier scan</div>
              <div class="dd">{new Date(cartState.last_seen_at!).toLocaleString('fr-FR')}</div>
            </div>
            <div>
              <div class="dt">Signalements</div>
              <div class="dd">{cartState.missed_count}</div>
            </div>
          </div>
        {:else}
          <p class="empty" style="text-align:left;padding:0">Jamais scanné.</p>
        {/if}

        <div class="row wrap" style="margin-top:.9rem">
          <span class="pill {cart.status === 'active' ? 'ok' : ''}">
            {cart.status === 'active' ? 'en service' : cart.status === 'lost' ? 'perdu' : 'retiré'}
          </span>
          {#if cartState.missing_since}
            <span class="pill danger">manquant depuis {new Date(cartState.missing_since).toLocaleDateString('fr-FR')}</span>
            <button class="slim no-print" onclick={() => (finding = true)}>Je l'ai retrouvé</button>
          {/if}
          {#if cartState.stale}
            <span class="pill warn">pas vu depuis plus de {store.staleAfterDays} j</span>
          {/if}
        </div>
      </div>
    </div>
  </div>

  {#if store.isAdmin}
    <div class="card no-print">
      <h2>Administration</h2>
      {#if error}<div class="banner danger">{error}</div>{/if}
      <div class="row wrap">
        <button class="slim" onclick={() => { newName = cart.name; renaming = true; }}>Renommer</button>
        {#if cart.status === 'active'}
          <button class="slim" disabled={busy} onclick={() => setStatus('lost')}>Déclarer perdu</button>
          <button class="slim" disabled={busy} onclick={() => setStatus('decommissioned')}>Retirer du service</button>
        {:else}
          <button class="slim" disabled={busy} onclick={() => setStatus('active')}>Remettre en service</button>
        {/if}
      </div>
      <p class="tiny muted" style="margin:.5rem 0 0">
        Pas de suppression : l'étiquette imprimée existe physiquement, et un scan réel doit dire
        « retiré en mars » plutôt que « code inconnu ».
      </p>
    </div>
  {/if}

  <div class="card">
    <h2>Historique ({history.length})</h2>
    {#if history.length === 0}
      <p class="empty">Aucun scan enregistré sur cet appareil.</p>
    {:else}
      <ul class="list">
        {#each history as e (e.id)}
          <li>
            <button class="row-link" onclick={() => router.go(`rounds/${e.round_id}`)}>
              <span class="small">{new Date(e.at).toLocaleString('fr-FR')}</span>
            </button>
            <span class="tiny muted truncate">{store.siteName(e.site_code)}</span>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

{#if finding}
  <CorrectionSheet
    site_code={cartState?.current_site ?? null}
    cart_code={code}
    onclose={() => (finding = false)}
  />
{/if}

{#if renaming}
  <div class="overlay" role="dialog" aria-modal="true" aria-label="Renommer">
    <div class="sheet">
      <h2>Renommer</h2>
      <label class="field"><span>Nom</span><input bind:value={newName} /></label>
      <div class="row">
        <button class="primary grow" onclick={rename} disabled={busy || !newName.trim()}>Enregistrer</button>
        <button class="ghost" onclick={() => (renaming = false)}>Annuler</button>
      </div>
    </div>
  </div>
{/if}

<style>
  .detail-grid { display: flex; flex-direction: column; gap: 1rem; }
  .detail-grid > .qr { width: 148px; }
  @media (min-width: 560px) {
    .detail-grid { flex-direction: row; align-items: flex-start; gap: 1.25rem; }
    .detail-grid > .qr { width: 164px; }
  }
</style>
